import { useState, useEffect, useMemo } from 'react';
import StatCard from "../../../components/common/StatCard";
import ComponentCard from "../../../components/common/ComponentCard";
import Button from "../../../components/ui/button/Button";
import Input from "../../../components/form/input/InputField";
import { DollarSign, FileText, Plus, Download, TrendingUp, AlertCircle, RefreshCw, ShoppingBag, Edit3, Check, Info, Calendar, Truck, Package, Search, BarChart, Users, Tag, User } from 'lucide-react';
import useApi from "../../../hooks/useApi";
import GastosList from "./GastosList";
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';
import { GastoStats } from '../types/index';
import { Gasto } from '../../../hooks/useApi';
import { Modal } from "../../../components/ui/modal";
import CreateGastoModal from "./CreateGastoModal";
import ProveedoresModal from "./ProveedoresModal";

interface ExportFilters {
  startDate: string;
  endDate: string;
  categoria: string;
  proveedor: string;
}

export default function GastosView() {
  const { 
    formatCurrency,
    getGastos,
    getGastoStats,
    exportGastos
  } = useApi();
  
  // State management
  const [gastos, setGastos] = useState<Gasto[]>([]);
  const [stats, setStats] = useState<GastoStats>({
    totalGastos: 0,
    gastosDelMes: 0,
    gastosDelMesAnterior: 0,
    porCategoria: {
      transporte: 0,
      suministros: 0,
      comisiones: 0,
      servicios: 0,
      equipos: 0,
      alquiler: 0,
      otros: 0
    },
    tendencias: {
      gastos: 0
    },
    totalProveedores: 0
  });
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showProveedorDropdown, setShowProveedorDropdown] = useState(false);
  const [showProveedoresModal, setShowProveedoresModal] = useState(false);
  const [exportFilters, setExportFilters] = useState({
    fechaInicio: format(new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1), 'yyyy-MM-dd'),
    fechaFin: format(new Date(), 'yyyy-MM-dd'),
    categoria: '',
    proveedor: ''
  });
  const [proveedores, setProveedores] = useState([]);
  const [categorias] = useState([
    { value: 'transporte', label: 'Transporte' },
    { value: 'suministros', label: 'Suministros' },
    { value: 'comisiones', label: 'Comisiones' },
    { value: 'servicios', label: 'Servicios' },
    { value: 'equipos', label: 'Equipos' },
    { value: 'alquiler', label: 'Alquiler' },
    { value: 'otros', label: 'Otros' }
  ]);

  // Get unique providers from gastos
  const uniqueProviders = useMemo(() => {
    const providers = new Set(gastos.map(gasto => gasto.proveedor));
    return Array.from(providers).sort();
  }, [gastos]);

  // Filter providers based on search
  const filteredProviders = useMemo(() => {
    if (!exportFilters.proveedor) return uniqueProviders;
    return uniqueProviders.filter(provider => 
      provider.toLowerCase().includes(exportFilters.proveedor.toLowerCase())
    );
  }, [uniqueProviders, exportFilters.proveedor]);

  // Load initial data
  useEffect(() => {
    refreshData();
  }, []);

  const refreshStats = async () => {
    try {
      setLoading(true);
      const response = await getGastoStats();
      setStats(response);
    } catch (error) {
      console.error('Error al obtener estadísticas:', error);
      toast.error('Error al cargar estadísticas');
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    try {
      setLoading(true);
      
      // Fetch gastos and stats in parallel
      await Promise.all([
        refreshStats(),
        getGastos().then(gastosData => {
          setGastos(gastosData as any);
        })
      ]);
      
      setLoading(false);
    } catch (error) {
      toast.error(
        'Error al cargar los datos. Por favor, verifique su conexión e inténtelo de nuevo.',
        {
          duration: 5000,
          icon: <AlertCircle className="w-4 h-4 text-red-500" />,
        }
      );
      setLoading(false);
    }
  };

  const handleExportData = async () => {
    try {
      setLoading(true);
      const response = await exportGastos({
        startDate: exportFilters.fechaInicio,
        endDate: exportFilters.fechaFin,
        categoria: exportFilters.categoria,
        proveedor: exportFilters.proveedor
      } as ExportFilters);

      // Get the response as an array buffer
      const arrayBuffer = await response.arrayBuffer();
      const blob = new Blob([arrayBuffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `gastos_${format(new Date(), 'yyyy-MM-dd')}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success('Datos exportados correctamente');
      setShowExportModal(false);
    } catch (error) {
      toast.error('Error al exportar datos');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenExportModal = () => {
    setShowExportModal(true);
  };

  const handleFilterChange = (value: string, name: string) => {
    setExportFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectProveedor = (provider: string) => {
    setExportFilters(prev => ({ ...prev, proveedor: provider }));
    setShowProveedorDropdown(false);
  };

  // Get primary category
  const getPrimaryCategory = (): [string, number] => {
    const categories = Object.entries(stats.porCategoria);
    if (categories.length === 0) return ['', 0];
    
    return categories.reduce((max, current) => {
      return current[1] > max[1] ? current : max;
    }, ['', 0] as [string, number]);
  };

  const primaryCategory = getPrimaryCategory();
  const getCategoryLabel = (value: string) => {
    const category = categorias.find(cat => cat.value === value);
    return category ? category.label : value;
  };

  // Add handler for opening providers modal
  const handleOpenProveedoresModal = () => {
    setShowProveedoresModal(true);
  };

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">
          <div className="px-6 py-5">
            <StatCard
              title="Gastos totales"
              value={loading ? '---' : formatCurrency(stats.totalGastos)}
              trend={0}
              icon={<DollarSign className="w-6 h-6" />}
              loading={loading}
            />
          </div>
        </div>
        <div className="rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">
          <div className="px-6 py-5">
            <StatCard
              title="Gastos del mes"
              value={loading ? '---' : formatCurrency(stats.gastosDelMes)}
              trend={stats.gastosDelMesAnterior > 0 ? ((stats.gastosDelMes - stats.gastosDelMesAnterior) / stats.gastosDelMesAnterior) * 100 : 0}
              icon={<TrendingUp className="w-6 h-6" />}
              loading={loading}
            />
          </div>
        </div>
        <div className="rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">
          <div className="px-6 py-5">
            <StatCard
              title={
                <div className="flex items-center justify-between w-full">
                  <span>Proveedores activos</span>
                </div>
              }
              value={loading ? '---' : (stats.totalProveedores !== undefined ? stats.totalProveedores.toString() : '0')}
              trend={0}
              icon={<Users className="w-6 h-6" />}
              loading={loading}
            />
          </div>
        </div>
        <div className="rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">
          <div className="px-6 py-5">
            <StatCard
              title="Comparación mensual"
              value={loading ? '---' : (stats.tendencias && stats.tendencias.gastos !== undefined) ? 
                (stats.tendencias.gastos > 0 ? `+${stats.tendencias.gastos.toFixed(2)}%` : `${stats.tendencias.gastos.toFixed(2)}%`) : '0%'}
              trend={!loading && stats.tendencias ? stats.tendencias.gastos : 0}
              icon={<BarChart className="w-6 h-6" />}
              loading={loading}
            />
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
        <div className="xl:col-span-3">
          <ComponentCard title="Registro de gastos" className='h-full'>
            <GastosList 
              gastos={gastos as any} 
              onDataChange={refreshData} 
              onCreateClick={() => setShowCreateModal(true)}
            />
          </ComponentCard>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <ComponentCard title="Acciones rápidas">
            <div className="space-y-3 p-4">
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start"
                startIcon={<Download className="w-4 h-4" />}
                onClick={handleOpenExportModal}
              >
                Exportar datos
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start"
                startIcon={<Truck className="w-4 h-4" />}
                onClick={handleOpenProveedoresModal}
              >
                Gestionar proveedores
              </Button>
            </div>
          </ComponentCard>

          <ComponentCard title="Distribución por categoría">
            <div className="p-4">
              <div className="space-y-3">
                {categorias.map(categoria => {
                  // Calculate percentage of total
                  const amount = stats.porCategoria[categoria.value] || 0;
                  const percentage = !loading && stats.totalGastos > 0 
                    ? Math.round((amount / stats.totalGastos) * 100) 
                    : 0;
                  
                  // Determine category color
                  const getCategoryColor = (catValue: string) => {
                    const colors = {
                      transporte: 'bg-purple-500 dark:bg-purple-600',
                      suministros: 'bg-blue-500 dark:bg-blue-600',
                      comisiones: 'bg-amber-500 dark:bg-amber-600',
                      servicios: 'bg-emerald-500 dark:bg-emerald-600',
                      equipos: 'bg-indigo-500 dark:bg-indigo-600',
                      alquiler: 'bg-rose-500 dark:bg-rose-600',
                      otros: 'bg-gray-500 dark:bg-gray-600'
                    };
                    return colors[catValue as keyof typeof colors] || 'bg-gray-500 dark:bg-gray-600';
                  };
                  
                  const categoryColor = getCategoryColor(categoria.value);
                  
                  return (
                    <div key={categoria.value} className="space-y-1">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <div className={`w-2 h-2 rounded-full ${categoryColor}`}></div>
                          <span className="text-sm text-gray-700 dark:text-gray-300">
                            {categoria.label}
                          </span>
                        </div>
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {loading ? '---' : formatCurrency(amount || 0)}
                        </span>
                      </div>
                      
                      {/* Simple bar chart representation */}
                      {!loading && (
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1">
                          <div 
                            className={`${categoryColor} h-1 rounded-full`} 
                            style={{ width: isNaN(percentage) ? '0%' : `${percentage}%` }}
                          ></div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              
              {/* Total section */}
              {!loading && (
                <div className="pt-3 mt-3 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Total</span>
                    <span className="text-sm font-bold text-gray-900 dark:text-white">
                      {formatCurrency(stats.totalGastos || 0)}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </ComponentCard>
        </div>
      </div>

      {/* Export Modal */}
      <Modal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        title="Exportar gastos a Excel"
      >
        <div className="space-y-4 pb-2">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Seleccione el período y los filtros para exportar los gastos
          </p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="fechaInicio" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                <Calendar className="w-4 h-4 text-primary-500 inline-block mr-1.5" />
                Fecha inicio
              </label>
              <input
                type="date"
                id="fechaInicio"
                name="fechaInicio"
                value={exportFilters.fechaInicio}
                onChange={(e) => handleFilterChange(e.target.value, 'fechaInicio')}
                className="block w-full shadow-sm sm:text-sm focus:ring-primary-500 focus:border-primary-500 border-gray-300 rounded-md dark:bg-gray-800 dark:border-gray-700 dark:text-white"
              />
            </div>
            <div>
              <label htmlFor="fechaFin" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                <Calendar className="w-4 h-4 text-primary-500 inline-block mr-1.5" />
                Fecha fin
              </label>
              <input
                type="date"
                id="fechaFin"
                name="fechaFin"
                value={exportFilters.fechaFin}
                onChange={(e) => handleFilterChange(e.target.value, 'fechaFin')}
                className="block w-full shadow-sm sm:text-sm focus:ring-primary-500 focus:border-primary-500 border-gray-300 rounded-md dark:bg-gray-800 dark:border-gray-700 dark:text-white"
              />
            </div>
          </div>
          
          <div>
            <label htmlFor="categoria" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              <Tag className="w-4 h-4 text-primary-500 inline-block mr-1.5" />
              Categoría
            </label>
            <select
              id="categoria"
              name="categoria"
              value={exportFilters.categoria}
              onChange={(e) => handleFilterChange(e.target.value, 'categoria')}
              className="block w-full shadow-sm sm:text-sm focus:ring-primary-500 focus:border-primary-500 border-gray-300 rounded-md dark:bg-gray-800 dark:border-gray-700 dark:text-white"
            >
              <option value="">Todas las categorías</option>
              {categorias.map(c => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label htmlFor="proveedor" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              <User className="w-4 h-4 text-primary-500 inline-block mr-1.5" />
              Proveedor
            </label>
            <div className="relative">
              <Input
                type="text"
                id="proveedor"
                name="proveedor"
                value={exportFilters.proveedor}
                onChange={(e) => {
                  handleFilterChange(e.target.value, 'proveedor');
                  setShowProveedorDropdown(true);
                }}
                onFocus={() => setShowProveedorDropdown(true)}
                placeholder="Buscar proveedor..."
                className="w-full"
              />
              {exportFilters.proveedor.length > 0 && showProveedorDropdown && (
                <div className="absolute z-30 w-full mt-1 bg-white dark:bg-gray-800 shadow-lg border border-gray-200 dark:border-gray-700 rounded-md max-h-60 overflow-y-auto">
                  {filteredProviders.length > 0 ? (
                    filteredProviders.map((provider) => (
                      <div
                        key={provider}
                        className="px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer text-sm"
                        onClick={() => handleSelectProveedor(provider)}
                      >
                        <div className="flex items-center">
                          <User className="w-4 h-4 text-gray-500 dark:text-gray-400 mr-2" />
                          <span className="text-gray-800 dark:text-gray-200">{provider}</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">
                      No se encontraron proveedores
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          
          <div className="pt-4 flex justify-end space-x-3">
            <Button
              variant="outline"
              onClick={() => setShowExportModal(false)}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={handleExportData}
              disabled={loading}
            >
              {loading ? 'Exportando...' : 'Exportar'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Create Gasto Modal */}
      <CreateGastoModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={refreshData}
      />

      {/* Add the providers modal */}
      <ProveedoresModal 
        isOpen={showProveedoresModal} 
        onClose={() => setShowProveedoresModal(false)} 
        gastos={gastos}
      />
    </div>
  );
} 